import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { CheckCircle } from 'lucide-react'

import { BookingSummary } from '@/components/booking-summary'
import { useHotelAvailability } from '@/components/HotelAvailabilityContext'
import { PetFormData } from '@/features/booking/types/booking.types'
import { useAuth } from '@/shared/auth'
import { isHairdressingService } from '@/shared/types/service-checkers'
import { hasHighSeasonDays } from '@/shared/utils/pricing'
import { Button } from '@/shared/ui/button'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'

export default function BookingConfirmationPage() {
    const { t } = useTranslation()
    const { reservationId } = useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { highSeasonPeriods } = useHotelAvailability()
    console.log('BookingConfirmationPage', reservationId)
    const reservation = useMemo(() => {
        return JSON.parse(localStorage.getItem('bookedReservation') || '{}')
    }, [])
    const isLoading = false

    const isHotelHighSeason =
        reservation.type === 'hotel' &&
        reservation.checkInDate &&
        reservation.checkOutDate &&
        hasHighSeasonDays(
            { from: new Date(reservation.checkInDate), to: new Date(reservation.checkOutDate) },
            highSeasonPeriods,
        )

    useEffect(() => {
        if (!reservation && !isLoading) {
            navigate('/booking')
        }
    }, [reservation, navigate])

    if (!reservation || isLoading) return null

    const renderContent = () => {
        if (reservation.type === 'hotel') {
            const petsFormData: PetFormData[] = reservation.pets.map(pet => ({
                name: pet.name,
                breed: pet.breed,
                weight: pet.weight,
                size: pet.size,
                age: '0',
                personality: '',
                sex: pet.sex || 'M',
                isNeutered: pet.isNeutered || false,
            }))

            return (
                <div className='py-4'>
                    <BookingSummary
                        dates={{
                            from: new Date(reservation.checkInDate),
                            to: new Date(reservation.checkOutDate),
                        }}
                        pets={petsFormData}
                        services={reservation.additionalServices}
                        clientClassification={reservation.client?.classification}
                    />
                </div>
            )
        } else {
            // Encontrar el servicio de peluquería
            const hairdressingService = reservation.additionalServices.find(isHairdressingService)
            const services = hairdressingService?.services || []

            return (
                <div className='space-y-6 py-4'>
                    <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                        <div className='space-y-4 rounded-lg border p-4'>
                            <h3 className='font-semibold'>
                                {t('booking.confirmation.grooming.appointmentDetails', 'Detalles de la cita')}
                            </h3>
                            <div className='space-y-2'>
                                <div>
                                    <p className='text-sm text-muted-foreground'>
                                        {t('booking.confirmation.grooming.date', 'Fecha')}
                                    </p>
                                    <p className='font-medium'>{reservation.date}</p>
                                </div>
                                <div>
                                    <p className='text-sm text-muted-foreground'>
                                        {t('booking.confirmation.grooming.time', 'Hora')}
                                    </p>
                                    <p className='font-medium'>{reservation.time}</p>
                                </div>
                            </div>
                        </div>

                        <div className='space-y-4 rounded-lg border p-4'>
                            <h3 className='font-semibold'>
                                {t('booking.confirmation.grooming.petDetails', 'Detalles de la mascota')}
                            </h3>
                            <div className='space-y-2'>
                                <div>
                                    <p className='text-sm text-muted-foreground'>
                                        {t('booking.confirmation.grooming.pet', 'Mascota')}
                                    </p>
                                    <p className='font-medium'>
                                        {reservation.pet.name} ({reservation.pet.breed})
                                    </p>
                                </div>
                                <div>
                                    <p className='text-sm text-muted-foreground'>
                                        {t('booking.confirmation.grooming.size', 'Tamaño')}
                                    </p>
                                    <p className='font-medium'>{reservation.pet.size}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='rounded-lg border p-4'>
                        <h3 className='mb-4 font-semibold'>
                            {t('booking.confirmation.grooming.services', 'Servicios seleccionados')}
                        </h3>
                        <div className='space-y-2'>
                            {services.map(service => (
                                <div key={service} className='flex items-center space-x-2'>
                                    <CheckCircle className='h-4 w-4 text-green-500' />
                                    <span>{t(`booking.confirmation.grooming.services.${service}`, service)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {reservation.totalPrice > 0 && (
                        <div className='rounded-lg border p-4'>
                            <h3 className='mb-4 font-semibold'>
                                {t('booking.confirmation.grooming.pricing', 'Precio')}
                            </h3>
                            <div className='flex items-center justify-between'>
                                <span className='text-muted-foreground'>
                                    {t('booking.confirmation.grooming.totalPrice', 'Precio total')}
                                </span>
                                <span className='text-lg font-semibold'>€{reservation.totalPrice.toFixed(2)}</span>
                            </div>
                        </div>
                    )}
                </div>
            )
        }
    }

    return (
        <div className='container mx-auto max-w-4xl p-4'>
            <div className='absolute right-4 top-4 z-50'>
                <LanguageSwitchButton />
            </div>
            <img src='/dondersteen-logo.png' alt='Dondersteen Logo' className='mx-auto mb-8 h-24' />
            <div className='rounded-lg border bg-card p-6 text-card-foreground shadow-sm'>
                <div className='flex flex-col items-center gap-4 text-center'>
                    <CheckCircle className='h-12 w-12 text-green-500' />
                    <h1 className='text-2xl font-bold'>{t('booking.confirmation.title', 'Solicitud Registrada')}</h1>
                    <div className='space-y-2'>
                        <p className='text-lg font-semibold'>
                            {t('booking.confirmation.thanks', '¡Gracias {{name}} por elegir nuestro servicio!', {
                                name: reservation.client?.name?.split(' ')[0],
                            })}
                        </p>
                        <p className='text-muted-foreground'>
                            {t(
                                'booking.confirmation.pending',
                                'Recuerda que esto es solo una solicitud, y la reserva no está confirmada.',
                            )}
                        </p>
                        {reservation.type === 'hotel' && (
                            <div className='mt-4 w-full max-w-lg space-y-3 text-left text-sm text-muted-foreground'>
                                <p>
                                    {t(
                                        'booking.confirmation.hotel.availability',
                                        'Estamos comprobando la disponibilidad. En breve te contactaremos por WhatsApp para confirmar o informarte de las opciones.',
                                    )}
                                </p>
                                <div>
                                    <p className='mb-1 font-medium text-foreground'>
                                        {t(
                                            'booking.confirmation.hotel.requirementsTitle',
                                            'Para que tu perro pueda alojarse con nosotros:',
                                        )}
                                    </p>
                                    <ul className='list-disc space-y-1 pl-5'>
                                        <li>
                                            {t(
                                                'booking.confirmation.hotel.requirementVaccines',
                                                'Vacunas al día (incluida Bordetella), desparasitado y protegido frente a parásitos. Envíanos por WhatsApp una foto de su cartilla.',
                                            )}
                                        </li>
                                        <li>
                                            {t(
                                                'booking.confirmation.hotel.requirementSocial',
                                                'Sociable con otros perros (parques compartidos por tamaño).',
                                            )}
                                        </li>
                                        <li>
                                            {t(
                                                'booking.confirmation.hotel.requirementHandling',
                                                'Que se deje manipular con normalidad por personas desconocidas.',
                                            )}
                                        </li>
                                    </ul>
                                </div>
                                {isHotelHighSeason && (
                                    <p className='rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200'>
                                        {t(
                                            'booking.confirmation.hotel.highSeasonNote',
                                            '⚠️ En temporada alta, la reserva solo quedará confirmada de forma definitiva una vez recibido el pago anticipado del 50% del importe de la estancia.',
                                        )}
                                    </p>
                                )}
                                <p className='text-foreground'>
                                    {t(
                                        'booking.confirmation.hotel.closing',
                                        '¡Estamos deseando conocer a tu peludo y darle la bienvenida a nuestro resort!',
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                {renderContent()}
                <div className='mt-6 flex justify-center'>
                    <Button
                        variant='outline'
                        onClick={() => {
                            if (user?.isStaff) {
                                navigate('/panel-interno')
                            } else {
                                window.location.href = 'https://dondersteen-resort.com/'
                            }
                        }}
                    >
                        {t('booking.confirmation.close', 'Volver al inicio')}
                    </Button>
                </div>
            </div>
        </div>
    )
}
